import React, { useMemo, useState } from 'react';
import { Drawer, Input, Select, Popconfirm, Table, Tag, Empty } from 'antd';
import type { ColumnsType, SorterResult } from 'antd/es/table/interface';
import { useNavigate } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { Plus, PenLine, ExternalLink, Trash2, Search } from 'lucide-react';
import { Button } from '@/components/ui/button';
import dayjs from 'dayjs';
import { useDeleteContent } from '@/hooks/useBlogCreationAPI';
import { contentPlannerKeys } from '@/hooks/useContentPlannerAPI';
import type { CalendarItem } from '@/types/contentPlanner';
import { cn } from '@/lib/utils';
import { StatusTag } from '../shared/StatusBadge';
import { GradeBadge } from '../shared/GradeBadge';
import { universalBlogEditorPath } from '@/utils/editorRoutes';

// ── Status helpers ─────────────────────────────────────────────────────────

const STATUS_ORDER: Record<string, number> = {
  pending: 0, generating: 1, draft: 2, completed: 3, published: 4, failed: 5,
};

function WPTag({ status }: { status: string | null }) {
  if (!status) return <span className="text-xs text-muted-foreground">—</span>;
  const cfg: Record<string, { label: string; bg: string; color: string }> = {
    published:  { label: 'Live',        bg: 'var(--color-coral-light)', color: 'var(--color-coral-dark)' },
    scheduled:  { label: 'Scheduled',   bg: '#fef3c7',                   color: '#92400e' },
    pending:    { label: 'WP Pending',  bg: '#fef3c7',                   color: '#92400e' },
    publishing: { label: 'Publishing…', bg: 'var(--color-teal-light)',   color: 'var(--color-teal-dark)' },
    enhancing:  { label: 'Enhancing…',  bg: 'var(--color-teal-light)',   color: 'var(--color-teal-dark)' },
    failed:     { label: 'WP Failed',   bg: '#fee2e2',                   color: '#991b1b' },
  };
  const c = cfg[status];
  return c
    ? <Tag style={{ background: c.bg, color: c.color, border: 'none', margin: 0, fontSize: 11 }}>{c.label}</Tag>
    : null;
}

function ScoreCell({ score }: { score: number | null }) {
  if (score == null) return <span className="text-xs text-muted-foreground">—</span>;
  const color = score >= 80 ? 'var(--color-teal-dark)' : score >= 60 ? '#b45309' : 'var(--color-coral-dark)';
  return <span style={{ fontSize: 12, fontWeight: 600, color }}>{score}</span>;
}

// ── Row actions ────────────────────────────────────────────────────────────

function RowActions({ item, onDeleted }: { item: CalendarItem; onDeleted: (id: string) => void }) {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { mutate: deleteContent, isPending } = useDeleteContent();
  const [confirmOpen, setConfirmOpen] = useState(false);

  function handleDelete() {
    deleteContent(item.id, {
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: contentPlannerKeys.all });
        onDeleted(item.id);
      },
    });
  }

  return (
    <div className="flex items-center gap-0.5">
      <button
        title="Edit article"
        className="p-1.5 rounded hover:bg-teal-light text-muted-foreground hover:text-teal-dark transition-colors"
        onClick={(event) => {
          event.stopPropagation();
          navigate(universalBlogEditorPath(item.id));
        }}
      >
        <PenLine className="h-3.5 w-3.5" />
      </button>
      {item.wp_url && (
        <a
          href={item.wp_url}
          target="_blank"
          rel="noopener noreferrer"
          title="View live"
          className="p-1.5 rounded hover:bg-coral-light text-muted-foreground hover:text-coral-dark transition-colors"
          onClick={(e) => e.stopPropagation()}
        >
          <ExternalLink className="h-3.5 w-3.5" />
        </a>
      )}
      <Popconfirm
        open={confirmOpen}
        title="Delete article?"
        description="Published WP posts will also be removed."
        okText="Delete"
        okButtonProps={{ danger: true, loading: isPending }}
        cancelText="Cancel"
        onConfirm={handleDelete}
        onCancel={() => setConfirmOpen(false)}
        placement="leftTop"
      >
        <button
          title="Delete"
          className="p-1.5 rounded hover:bg-destructive/10 text-muted-foreground hover:text-destructive transition-colors"
          onClick={(event) => {
            event.stopPropagation();
            setConfirmOpen(true);
          }}
        >
          <Trash2 className="h-3.5 w-3.5" />
        </button>
      </Popconfirm>
    </div>
  );
}

// ── Main component ─────────────────────────────────────────────────────────

interface DayDetailDrawerProps {
  open: boolean;
  date: string | null;
  items: CalendarItem[];
  onClose: () => void;
  onCreateForDate?: (date: string) => void;
}

const STATUS_FILTER_OPTIONS = [
  { value: 'draft',      label: 'Draft' },
  { value: 'completed',  label: 'Completed' },
  { value: 'generating', label: 'Generating' },
  { value: 'pending',    label: 'Pending' },
  { value: 'published',  label: 'Published' },
  { value: 'failed',     label: 'Failed' },
];

export function DayDetailDrawer({ open, date, items, onClose, onCreateForDate }: DayDetailDrawerProps) {
  const navigate = useNavigate();
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState<string | null>(null);
  const [deletedIds, setDeletedIds] = useState<Set<string>>(new Set());

  // Reset filters when drawer closes
  React.useEffect(() => {
    if (!open) {
      setSearch('');
      setStatusFilter(null);
      setDeletedIds(new Set());
    }
  }, [open]);

  const filtered = useMemo(() => {
    const q = search.toLowerCase().trim();
    return items.filter((item) => {
      if (deletedIds.has(item.id)) return false;
      if (statusFilter && item.status !== statusFilter) return false;
      if (q) {
        const haystack = `${item.title ?? ''} ${item.keyword ?? ''}`.toLowerCase();
        if (!haystack.includes(q)) return false;
      }
      return true;
    });
  }, [items, search, statusFilter, deletedIds]);

  const columns: ColumnsType<CalendarItem> = [
    {
      key: 'title',
      title: 'Title / Keyword',
      ellipsis: true,
      sorter: (a, b) => {
        const la = (a.title || a.keyword || '').toLowerCase();
        const lb = (b.title || b.keyword || '').toLowerCase();
        return la.localeCompare(lb);
      },
      render: (_: unknown, r: CalendarItem) => {
        const label = r.title || r.keyword || 'Untitled';
        return (
          <div>
            <div className="text-sm font-medium leading-snug line-clamp-2">{label}</div>
            {r.template_name && (
              <div className="text-[11px] text-muted-foreground mt-0.5">{r.template_name}</div>
            )}
          </div>
        );
      },
    },
    {
      key: 'status',
      title: 'Status',
      width: 108,
      sorter: (a, b) => (STATUS_ORDER[a.status] ?? 9) - (STATUS_ORDER[b.status] ?? 9),
      render: (_: unknown, r: CalendarItem) => <StatusTag status={r.status} />,
    },
    {
      key: 'publish_status',
      title: 'WP',
      width: 96,
      render: (_: unknown, r: CalendarItem) => <WPTag status={r.publish_status} />,
    },
    {
      key: 'grade',
      title: 'Grade',
      width: 68,
      sorter: (a, b) => {
        const order = ['A', 'B', 'C', 'D', 'F'];
        return (order.indexOf(a.grade ?? 'F') ) - (order.indexOf(b.grade ?? 'F'));
      },
      render: (_: unknown, r: CalendarItem) => <GradeBadge grade={r.grade} size="md" showEmpty />,
    },
    {
      key: 'quality_score',
      title: 'Score',
      width: 62,
      sorter: (a, b) => (b.quality_score ?? -1) - (a.quality_score ?? -1),
      render: (_: unknown, r: CalendarItem) => <ScoreCell score={r.quality_score} />,
    },
    {
      key: 'word_count',
      title: 'Words',
      width: 68,
      sorter: (a, b) => (b.word_count ?? 0) - (a.word_count ?? 0),
      render: (_: unknown, r: CalendarItem) =>
        r.word_count != null
          ? <span className="text-xs">{r.word_count.toLocaleString()}</span>
          : <span className="text-xs text-muted-foreground">—</span>,
    },
    {
      key: 'actions',
      title: '',
      width: 88,
      render: (_: unknown, r: CalendarItem) => (
        <RowActions item={r} onDeleted={(id) => setDeletedIds((prev) => new Set([...prev, id]))} />
      ),
    },
  ];

  const formattedDate = date ? dayjs(date).format('dddd, MMMM D, YYYY') : '';

  return (
    <Drawer
      open={open}
      onClose={onClose}
      width={760}
      title={
        <div className="flex items-center justify-between gap-4">
          <div>
            <div className="font-semibold text-base">{formattedDate}</div>
            <div className="text-xs text-muted-foreground font-normal mt-0.5">
              {filtered.length} of {items.length} article{items.length !== 1 ? 's' : ''}
            </div>
          </div>
          {date && onCreateForDate && (
            <Button
              size="sm"
              className="gap-1.5 bg-coral hover:bg-coral-hover text-white shrink-0"
              onClick={() => onCreateForDate(date)}
            >
              <Plus className="h-3.5 w-3.5" />
              New Content
            </Button>
          )}
        </div>
      }
      styles={{
        body: { padding: 0, overflow: 'hidden', display: 'flex', flexDirection: 'column' },
      }}
    >
      {/* Filter bar — pinned at top; body doesn't scroll so no sticky needed */}
      <div className="flex items-center gap-2 px-4 py-3 border-b bg-warm-bg flex-shrink-0">
        <Input
          prefix={<Search className="h-3.5 w-3.5 text-muted-foreground" />}
          placeholder="Search title or keyword…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          allowClear
          style={{ flex: 1 }}
          size="small"
        />
        <Select
          placeholder="All statuses"
          value={statusFilter}
          onChange={(v) => setStatusFilter(v ?? null)}
          allowClear
          options={STATUS_FILTER_OPTIONS}
          style={{ width: 148 }}
          size="small"
        />
      </div>

      {/* Table — tbody scrolls internally; thead stays naturally above the scroll area */}
      <div style={{ flex: 1, overflow: 'hidden' }}>
        {items.length === 0 ? (
          <div className="flex items-center justify-center py-20">
            <Empty description="No articles scheduled for this day." image={Empty.PRESENTED_IMAGE_SIMPLE} />
          </div>
        ) : filtered.length === 0 ? (
          <div className="flex items-center justify-center py-20">
            <Empty description="No articles match your filters." image={Empty.PRESENTED_IMAGE_SIMPLE} />
          </div>
        ) : (
          <Table<CalendarItem>
            columns={columns}
            dataSource={filtered}
            rowKey="id"
            size="middle"
            pagination={false}
            scroll={{ x: 680, y: 'calc(100vh - 135px)' }}
            rowClassName="group cursor-pointer"
            onRow={(record) => ({
              onClick: () => navigate(universalBlogEditorPath(record.id)),
            })}
          />
        )}
      </div>
    </Drawer>
  );
}
